import { type FC, type PropsWithChildren } from 'react';
import { Controller, type Control, useFieldArray } from 'react-hook-form';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Autocomplete,
  Box,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { type EditAuditFormBackgroundData } from '#apps/client-web/pages/audit-form-background/types';
import { getFormErrorAttributes } from '#apps/client-web/app/helpers/react-hook-form';
import { getSuiteTypeLabel } from '#apps/client-web/features/audit-form-mechanical/constants';

import {
  SubFormDialog,
  type SubFormDialogProps,
} from '#packages/web/layouts/dialog/templates/sub-form';
import { Fab } from '#packages/web/foundations/buttons/fab';
import { DeleteConfirmationDialog } from '#packages/web/layouts/dialog/templates/delete-confirmation';
import { useDialog } from '#packages/web/hooks/use-dialog';
import { MessagePage } from '#packages/web/layouts/page/templates/message-page';
import { Message } from '#packages/web/foundations/feedback/message';

export type MurbSuitesFormDialogProps = Pick<
  SubFormDialogProps,
  'onClose' | 'open'
> & {
  control: Control<EditAuditFormBackgroundData>;
};

export const MurbSuitesFormDialog: FC<
  PropsWithChildren<MurbSuitesFormDialogProps>
> = ({ open, control, onClose }) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'general.murbSuites',
  });

  const deleteSuiteDialog = useDialog<number>();

  return (
    <SubFormDialog title="MURB Suites" open={open} onClose={onClose}>
      <Stack spacing={3} sx={{ pb: 10 }}>
        {fields.length === 0 && (
          <MessagePage
            message={<Message>No suites added yet. Tap + to add one.</Message>}
          />
        )}
        {fields.map((field, index) => (
          <Box key={field.id}>
            <Stack spacing={2} sx={{ width: '100%' }}>
              <Grid
                container
                alignItems="center"
                justifyContent="space-between">
                <Typography variant="body2" color="textSecondary">
                  Suite type {index + 1}
                </Typography>
                <IconButton
                  size="small"
                  color="error"
                  aria-label="remove suite"
                  onClick={() => deleteSuiteDialog.open(index)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Grid>
              <Grid container spacing={2}>
                <Grid size={{ xs: 8 }}>
                  <Controller
                    control={control}
                    name={`general.murbSuites.${index}.type`}
                    rules={{ maxLength: 120 }}
                    render={({ field, fieldState }) => (
                      <FormControl
                        fullWidth
                        {...getFormErrorAttributes(fieldState.error)}>
                        <Autocomplete
                          {...field}
                          {...getFormErrorAttributes(fieldState.error)}
                          getOptionLabel={(option) => getSuiteTypeLabel(option)}
                          options={[0, 1, 2, 3]}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Type"
                              variant="outlined"
                              {...getFormErrorAttributes(fieldState.error)}
                            />
                          )}
                          value={field.value ?? null}
                          onChange={(_, value) => field.onChange(value)}
                        />
                        <FormHelperText>
                          {fieldState.error?.message}
                        </FormHelperText>
                      </FormControl>
                    )}
                  />
                </Grid>
                <Grid size={{ xs: 4 }}>
                  <Controller
                    control={control}
                    name={`general.murbSuites.${index}.num`}
                    render={({ field, fieldState }) => (
                      <FormControl
                        fullWidth
                        {...getFormErrorAttributes(fieldState.error)}>
                        <TextField
                          {...field}
                          label="Number"
                          type="number"
                          variant="outlined"
                          {...getFormErrorAttributes(fieldState.error)}
                        />
                        <FormHelperText>
                          {fieldState.error?.message}
                        </FormHelperText>
                      </FormControl>
                    )}
                  />
                </Grid>
              </Grid>
            </Stack>
            {index < fields.length - 1 && <Divider sx={{ mt: 3 }} />}
          </Box>
        ))}
      </Stack>

      <Fab tooltip="Add suite" onClick={() => append({ type: 0, num: 1 })} />

      <DeleteConfirmationDialog
        open={deleteSuiteDialog.isOpen}
        dialogTitle="Remove Suite"
        confirmButtonLabel="Remove"
        onConfirm={() => {
          remove(deleteSuiteDialog.data!);
          deleteSuiteDialog.close();
        }}
        onCancel={() => deleteSuiteDialog.close()}
      />
    </SubFormDialog>
  );
};
